import { useForm } from "../../../../hooks/useForm/useForm";
import { FormProps } from "../../../form-types";
import { Database, supabase } from "../../../../../../lib/supabase";
import { SupabaseWriteTableEnum } from "../../../../../../lib/supabase/supabaseTypes";
import { useState } from "react";
import { ImageInput } from "../input-components/image-input";
import { useCentralStore } from "@/dentlab/src/store/Central";
import { showNotification } from "@/dentlab/src/store/Central/selectors";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { StorageBucketsEnum } from "@/dentlab/src/types/enums";
import { Logger } from "@/lib/logger/Logger";
import { Button } from "@/dentlab/src/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/dentlab/src/components/ui/card";
import { Checkbox } from "@/dentlab/src/components/ui/checkbox";
import { Label } from "@/dentlab/src/components/ui/label";
import { CustomTextFieldShadcn } from "../../../form-input-components/custom-input-components/custom-text-field-shadcn.component";
import { TitleAndNameGroupShadcn } from "../../../form-input-components/groups/title-and-name.group";
import { Building2, User, FileImage } from "lucide-react";
import { AddressGroupShadcn } from "../../../form-input-components/groups/address.group";

export const ClientForm: React.FC<FormProps> = ({
	onSave = () => { },
	submitRef = null,
}) => {
	const { organizationId, upsertClient } = useCentralStore((state) => ({
		organizationId: state.organization?.id,
		upsertClient: state.upsertClient,
	}));
	const [profilePicFile, setProfilePicFile] = useState<File | null>(null);
	const navigate = useNavigate();
	const {
		handleInputChange,
		handleCheckboxChange,
		handleCreate,
		formData,
		formErrors,
		handleValueChange,
	} = useForm<Database["public"]["Tables"]["clients"]["Row"]>(
		{
			organization_id: organizationId,
		},
		SupabaseWriteTableEnum.CLIENTS,
		undefined,
		undefined,
		"Auftraggeber"
	);

	const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		if (profilePicFile) {
			if (!formData.avatar_path) {
				showNotification({
					message: "Fehler beim Hochladen des Avatars",
					type: "error",
				});
				return;
			}
			const { error } = await supabase.storage
				.from(StorageBucketsEnum.ORG_V1)
				.upload(
					`${organizationId}/${formData.avatar_path}`,
					profilePicFile
				);

			if (error) {
				showNotification({
					message: "Fehler beim Hochladen des Avatars",
					type: "error",
				});
				Logger.error(error, {}, "Error uploading avatar");
				return;
			}
		}
		delete formData.search_vector;
		const { success, data } = await handleCreate(event);
		if (success && data) {
			const client = data[0];
			delete client.search_vector;
			onSave(client);
			await upsertClient(client);
			navigate(`/client/${client.id}`);
		} else {
			showNotification({
				message: "Fehler beim Speichern des Kunden",
				type: "error",
			});
		}
	};

	const handleFileChange = (file: File | null) => {
		setProfilePicFile(file);
		handleValueChange("avatar_path", `avatars/${uuidv4()}/avatar`);
	};

	return (
		<form onSubmit={onSubmit} className="space-y-6">
			<Card className="shadow-none">
				<CardHeader>
					<CardTitle className="flex items-center gap-2">
						<FileImage className="h-5 w-5" />
						Profilinformationen
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-6">
					<div className="flex flex-col sm:flex-row gap-6">
						<div className="flex-shrink-0">
							<ImageInput
								bucket={StorageBucketsEnum.ORG_V1}
								onFileChange={handleFileChange}
								organizationId={organizationId ?? ""}
							/>
						</div>
						<div className="flex-1 space-y-4">
							<CustomTextFieldShadcn
								label="Kürzel"
								name="code"
								value={formData.code || ""}
								onChange={handleInputChange}
								required
								validationError={formErrors?.code}
								placeholder="z.B. ABC123"
								customHelperText="Eindeutiges Kürzel für den Kunde"
								classNameWrapper="max-w-xs"
								data-testid="client-code-input"
								aria-label="Kunden-Kürzel"
							/>
						</div>
					</div>
				</CardContent>
			</Card>

			<Card className="shadow-none">
				<CardHeader>
					<CardTitle className="flex items-center gap-2">
						{formData.is_company ? (
							<Building2 className="h-5 w-5" />
						) : (
							<User className="h-5 w-5" />
						)}
						Kontaktdaten
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-6">
					<div className="flex items-center space-x-2">
						<Checkbox
							id="is_company"
							checked={formData.is_company ? true : false}
							onCheckedChange={(checked) =>
								handleCheckboxChange({
									target: { name: "is_company", checked }
								} as any)
							}
							data-testid="is-company-checkbox"
							aria-label="Firmenadresse aktivieren"
						/>
						<Label
							htmlFor="is_company"
							className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
						>
							Firmenadresse
						</Label>
					</div>

					{!formData.is_company && (
						<TitleAndNameGroupShadcn
							formData={formData}
							formErrors={formErrors}
							onChange={handleInputChange}
							onValueChange={(args) => handleValueChange(args.name, args.value)}
							requiredFields={formData.is_company ? [] : ["last_name"]}
						/>
					)}

					<div className="space-y-4">
						<CustomTextFieldShadcn
							label={formData.is_company ? "Firma / Organisation" : "Firma / Organisation (optional)"}
							name="company_name"
							value={formData.company_name || ""}
							onChange={handleInputChange}
							required={formData.is_company}
							validationError={formErrors?.company_name}
							placeholder="Name der Firma oder Organisation"
							data-testid="company-name-input"
							aria-label="Firmenname"
						/>
					</div>

					<AddressGroupShadcn
						formData={formData}
						formErrors={{}}
						onChange={handleInputChange}
						onValueChange={(args) => handleValueChange(args.name, args.value)}
						requiredFields={["street", "city", "postal_code", "country"]}
						exclude={["phone_personal", "phone_business", "phone_mobile", "fax", "email"]}
					/>
				</CardContent>
			</Card>

			<div className="flex justify-end gap-4">
				<Button
					type="submit"
					data-testid="save-client-button"
					aria-label="Kunde speichern"
				>
					Kunde speichern
				</Button>
			</div>
		</form>
	);
};